import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  SimpleGrid,
  Flex,
  Icon,
  useColorModeValue,
  Card, // Import Card
  CardBody, // Import CardBody
  CardHeader, // Optional: Import CardHeader if needed
} from '@chakra-ui/react';
import { Database, Lightbulb, Users } from '@phosphor-icons/react';

export const ProblemsSolvedSection = () => {
  // Use theme tokens directly
  const sectionBg = useColorModeValue('bg.surface', 'bg.surface');
  const headingColor = useColorModeValue('interactive.primary', 'interactive.primary');
  const textColor = useColorModeValue('text.default', 'text.default');
  const textColorMuted = useColorModeValue('text.muted', 'text.muted');
  const iconBg = useColorModeValue('bg.accent', 'bg.accent');

  const problems = [
    {
      icon: Database,
      color: useColorModeValue('interactive.primary', 'interactive.primary'), // Example: Use primary color
      title: "Fragmented Information",
      description: "Centralizes scattered information about megalithic sites from various sources into one comprehensive platform."
    },
    {
      icon: Lightbulb,
      color: useColorModeValue('accent.gold', 'accent.gold'), // Example: Use accent gold
      title: "Documentation Challenges",
      description: "Provides a standardized system for documenting site features, locations, and characteristics."
    },
    {
      icon: Users,
      color: useColorModeValue('brand.secondary.500', 'brand.secondary.300'), // Example: Use secondary color
      title: "Community Collaboration",
      description: "Creates a platform for researchers and enthusiasts to collaborate, share findings, and validate observations."
    }
  ];

  return (
    <Box
      bg={sectionBg}
      py={{ base: 12, md: 16 }}
      position="relative" // Needed for pseudo-element
      _before={{ // Add background texture overlay
        content: '""',
        position: 'absolute',
        top: 0, left: 0, right: 0, bottom: 0,
        opacity: useColorModeValue(0.08, 0.12), // Slightly more visible on surface bg
        pointerEvents: 'none',
        bgImage: useColorModeValue('var(--texture-parchment)', 'var(--texture-leather)'),
        bgRepeat: 'repeat',
        bgSize: '500px',
        bgBlendMode: useColorModeValue('soft-light', 'multiply'),
        zIndex: 0,
      }}
    >
      <Container maxW="container.xl" position="relative" zIndex={1}> {/* Content needs higher z-index */}
        <VStack spacing={12} align="center">
          <VStack spacing={4} textAlign="center">
            <Heading as="h2" size="2xl" color={headingColor} fontFamily="heading">
              Problems Solved
            </Heading>
            <Text fontSize="lg" maxW="3xl" fontFamily="body" color={textColor}>
              Megalith Maps addresses several key challenges in the field of megalithic research and documentation:
            </Text>
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8} width="full">
            {problems.map((item, index) => (
              // Use the themed Card component
              <Card
                key={index}
                variant="journal" // Apply journal theme variant
                textAlign="center"
                transition="all 0.3s"
                 _hover={{
                   transform: 'translateY(-5px)', // Slightly more lift on hover
                   boxShadow: 'lg', // Use theme shadow
                 }}
                 position="relative" // Needed for pseudo-element
                 overflow="hidden" // Keep texture contained
                 _before={{ // Add internal texture
                   content: '""',
                   position: 'absolute',
                   top: 0, left: 0, right: 0, bottom: 0,
                   opacity: useColorModeValue(0.06, 0.1), // Subtle internal texture opacity
                   pointerEvents: 'none',
                   bgImage: 'var(--texture-aged)', // Use aged paper texture for journal cards
                   bgRepeat: 'repeat',
                   bgSize: '300px', // Adjust size
                   bgBlendMode: useColorModeValue('multiply', 'soft-light'), // Adjust blend mode
                   zIndex: 0,
                   borderRadius: 'inherit', // Ensure texture respects card border radius
                 }}
              >
                {/* CardBody provides padding and structure */}
                {/* Ensure CardBody content is above the texture */}
                <CardBody position="relative" zIndex={1}>
                  <VStack spacing={5}>
                    <Flex
                      w="60px"
                      h="60px"
                      bg={iconBg}
                      color={item.color} // Use defined color
                      borderRadius="full" // Circular icon background
                      justify="center"
                      align="center"
                      boxShadow="sm"
                    >
                      <Icon as={item.icon} boxSize={8} weight="duotone" />
                    </Flex>
                    <Heading size="lg" fontFamily="heading" color={textColor}>
                      {item.title}
                    </Heading>
                    <Text color={textColorMuted} fontFamily="body">
                      {item.description}
                    </Text>
                  </VStack>
                </CardBody>
              </Card>
            ))}
          </SimpleGrid>
        </VStack>
      </Container>
    </Box>
  );
};